import { addListener } from "~/lib/signals";
import Wasm from "../wasm";
import WasmStoreMapping from "../mapping";
import { watch, watchCoordinates, watchMapset, watchOphotoTimeline } from "~/state/util";
import { store } from "~/state/store";
import { setCoordinates } from "~/state/map/state";
import { deferPromise } from "~/util/general";
import MapContextMenu from "~/ui/controls/mapcontextmenu";
import { LAYER_MAPPING } from "../const";
import { MAP_OVERLAYS } from "~/state/map/constants";
import { TWasmBaseMaps, TWasmOverlay } from "~/interfaces";
import { IPoiData } from "../interfaces";
import { CONFIG } from "~/config";
import WasmHiddenPois from "../hidden-pois";
import WasmSharePosition from "../wasm-share-position";
import { onMapClick } from "./click";
import { onHover } from "./hover";
import { onDragEnd, onDragMove, onDragStart } from "./dnd";

export default class WasmController {
	_selector: string;

	_canvas: HTMLCanvasElement;

	_wasm: Wasm;

	_mapping: WasmStoreMapping;

	_afterLoad: boolean;

	_loadProm: ReturnType<typeof deferPromise>;

	_hiddenPoisLists: Array<WasmHiddenPois>;

	constructor() {
		this._selector = "wasm-canvas";
		this._afterLoad = false;
		this._loadProm = deferPromise();
		this._hiddenPoisLists = [];
	}

	start(loadWasm: boolean) {
		if (loadWasm) {
			this._build();
			this._run();
		} else {
			this._loadProm.resolve({});
		}

		return this._loadProm.promise;
	}

	get wasm() {
		return this._wasm;
	}

	createHiddenPoiList(name: string) {
		const hiddenList = new WasmHiddenPois(name);

		// pripojine wasm nebo pridame k pozdejsimu pridani
		if (this._wasm) {
			hiddenList.wasm = this._wasm;
		} else {
			this._hiddenPoisLists.push(hiddenList);
		}

		return hiddenList;
	}

	_build() {
		const canvas = document.createElement("canvas");

		canvas.classList.add(this._selector);
		canvas.style.width = `100%`;
		canvas.style.height = `100%`;

		const wasmParent = document.getElementById("wasm");

		wasmParent.append(canvas);
		document.getElementById("left3d").style.display = "none";
		document.getElementById("map").style.display = "none";

		const wasm = new Wasm({
			parentElem: wasmParent,
			// 1.5 prace, 2 macbook, standard 1
			dpi: 2,
			canvasQuery: `.${this._selector}`,
			onMapClick: data => {
				onMapClick(data, store, CONFIG, this._mapping);
			},
			onHover: data => {
				onHover(data, store, this._mapping);
			},
			onDragStart: (wasmId, event) => onDragStart(wasmId, event, store, this._mapping, wasm),
			onDragMove: (id, event) => {
				onDragMove(id, event, store, wasm);
			},
			onDragEnd: (id, event) => {
				onDragEnd(id, event, store, wasm);
			},
			onContextMenu: (event, poi, wasmId) => {
				this._contextMenu(event, poi, wasmId);
			},
			onMapRedraw: () => {
				if (!this._afterLoad) {
					return;
				}

				const position = this._wasm.getCenterZoom();

				store.dispatch(setCoordinates({
					longitude: position.lon,
					latitude: position.lat,
					zoom: position.zoom,
				}));
				Mapy.getComponent("history").save();
			},
		});

		// update velikosti
		addListener({}, "width-change", () => {
			wasm.syncPort();
		});

		this._wasm = wasm;
		this._canvas = canvas;
	}

	async _run() {
		await this._wasm.loadProm;

		this._wasm.mapControl.setTargetFps(60);

		// pripojime wasm
		for (const hiddenPoiList of this._hiddenPoisLists) {
			hiddenPoiList.wasm = this._wasm;
		}

		this._hiddenPoisLists.length = 0;

		this._mapping = new WasmStoreMapping(this._wasm);
		this._watchForMapChange();
		this._afterLoad = true;

		/* eslint-disable-next-line */
		new WasmSharePosition();

		this._loadProm.resolve({});
	}

	_watchForMapChange() {
		const curState = store.getState().map;
		const wt = watch(store.getState, "map", watchCoordinates);
		const wtMapset = watch(store.getState, "map", watchMapset);
		const wtOphotoTimeline = watch(store.getState, "map", watchOphotoTimeline);

		// zmeny souradnic
		store.subscribe(wt((newMap, oldMap) => {
			if (!newMap.ignore
				&& (
					newMap.longitude !== oldMap.longitude
					|| newMap.latitude !== oldMap.latitude
					|| newMap.zoom !== oldMap.zoom
				)
			) {
				// neumime animace
				this._wasm.setCenterZoom(newMap.longitude, newMap.latitude, newMap.zoom);

				store.dispatch(setCoordinates({
					animating: false,
				}));
			}
		}));

		// zmeny mapsetu a overlayu
		store.subscribe(wtMapset(newMap => {
			this._setMapset(newMap.mapset as TWasmBaseMaps, newMap.overlays, newMap.ophotoTimeline);
		}));

		// zmena stari ortofoto snimku
		store.subscribe(wtOphotoTimeline(newMap => {
			this._setMapset(newMap.mapset as TWasmBaseMaps, newMap.overlays, newMap.ophotoTimeline);
		}));

		this._wasm.setCenterZoom(curState.longitude, curState.latitude, curState.zoom);
		this._setMapset(curState.mapset as TWasmBaseMaps, curState.overlays, curState.ophotoTimeline);
	}

	_setMapset(mapset: TWasmBaseMaps, overlays: Array<MAP_OVERLAYS> = [], ophotoTimeline = "") {
		if (mapset in LAYER_MAPPING) {
			this._wasm.setMapstyle({
				base: mapset,
				overlays: [
					...overlays.includes(MAP_OVERLAYS.HILLSHADE) ? ["hillshade", "hillslope"] : [],
					...overlays.includes(MAP_OVERLAYS.PANORAMA) ? ["panoLayer"] : [],
					...overlays.includes(MAP_OVERLAYS.CADASTRAL) ? ["knLayer"] : [],
					...overlays.includes(MAP_OVERLAYS.TURIST) ? ["touristMode"] : [],
				] as Array<TWasmOverlay>,
				ophotoTimeline,
			});
		} else {
			// eslint-disable-next-line no-console
			console.error(`Unknown mapset: ${mapset} (overlays: ${overlays})`);
		}
	}

	async _screenshot() {
		const imgData = await this._wasm.getScreenshot();
		const img = new Image();

		img.addEventListener("load", () => {
			const width = img.naturalWidth / devicePixelRatio >>> 0;
			const height = img.naturalHeight / devicePixelRatio >>> 0;

			Object.assign(img.style, {
				"position": "absolute",
				"inset": "0 0 0 0",
				"z-index": 100,
				"width": `${width}px`,
				"height": `${height}px`,
			});
			document.body.append(img);
		});
		img.src = imgData;
		img.addEventListener("click", () => {
			img.remove();
		});
	}

	_contextMenu(event: PointerEvent, pois: Array<IPoiData>, wasmId: number) {
		// zastavime udalost
		event.preventDefault();
		event.stopPropagation();

		// ziskame souradnice v mape
		const { clientX, clientY } = event;
		const pos = this._wasm.mapControl.screenPosToMapPos({
			x: clientX,
			y: clientY,
		});

		const wgs = this._wasm.module.GeoMath.mercatorToWGS(pos);
		// vyrobime falesny element, na ktery se navaze menu
		const referenceElement = {
			getBoundingClientRect() {
				return {
					width: 0,
					height: 0,
					x: clientX,
					y: clientY,
					top: clientY,
					left: clientX,
					right: clientX,
					bottom: clientY,
				};
			},
		};
		// vyrobime menu
		const contextMenu = new MapContextMenu({
			anchor: referenceElement as HTMLElement,
			position: "bottom-start",
			clickOutIgnoredElements: [],
			onClose: () => {
				contextMenu.destroy();
			},
			onOpen: () => {
				Mapy.getComponent("logger").log("menu", {
					eventx: wgs.x.toFixed(6),
					eventy: wgs.y.toFixed(6),
				});
			},
			longitude: wgs.x,
			latitude: wgs.y,
			pois,
			wasmStoreId: this._mapping.getWasmToStoreId(wasmId),
		});

		// ukazeme menu
		contextMenu.open();
	}
}
